import { Fragment, useMemo, useState } from "react"

// ** Third Party Components
import { z } from "zod"
import { he } from "date-fns/locale"
import { Loader } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Add, Calendar, CloseCircle } from "iconsax-reactjs"

// ** Theme Components
import { Label } from "@pma/components/common/label"
import CalendarDate from "@pma/components/common/CalendarDate"
import { Avatar, AvatarImage } from "@pma/components/common/avatar"
import { CalendarElement } from "@pma/components/common/customCalendar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@pma/components/common/tooltip"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@pma/components/common/form"
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@pma/components/common/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pma/components/common/select"

// ** Store
import { useAuth } from "@pma/stores/auth-store"
import { useCommonStore } from "@pma/stores/common-store"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Services
import { createNewTaskService, getGroupWiseTaskListService } from "@pma/services/task"
import { getTaskPrioritiesListService, getTaskStatusListService } from "@pma/services/common"
import { getProjectListService } from "@pma/services/project"

// ** Types
import { DictType } from "@pma/types"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import TeamMembersDialog from "@pma/components/TeamMembers"

const taskSchema = z.object({
  title: z.string().min(1, "Task name is required"),
  description: z.string(),
  project_id: z.string().min(1, "Project is required"),
  tasks_group_id: z.string(),
  status_id: z.string(),
  priority_id: z.string(),
  start_at: z.string(),
  finish_to: z.string(),
  responsible_id: z.array(z.number()),
})

type TaskFormValues = z.infer<typeof taskSchema>

interface TaskInsertDialogProps {
  isMobile?: boolean
  onClose: () => void
}

const TaskInsertDialog = ({ isMobile = false, onClose }: TaskInsertDialogProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const { taskStatusList, taskPrioritiesList } = useCommonStore()

  const isHebrew = locale === "he"
  const queryClient = useQueryClient()
  const orgId = authConfig?.last_organization_id

  const [showMoreFields, setShowMoreFields] = useState<boolean>(false)
  const [selectedAssignees, setSelectedAssignees] = useState<any[]>([])
  const [assigneesDialog, setAssigneesDialog] = useState<boolean>(false)

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: "",
      description: "",
      project_id: "",
      tasks_group_id: "0",
      status_id: "",
      priority_id: "",
      start_at: "",
      finish_to: "",
      responsible_id: [],
    },
  })

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form

  const selectedProjectId = watch("project_id")

  const { data: projects, isLoading: isProjectLoading } = useQuery({
    queryKey: ["get-project-list-for-task", orgId],
    queryFn: () =>
      getProjectListService({
        by_organization_id: orgId,
        paging_start_page: 0,
        paging_per_page: 999,
      }),
    enabled: !!orgId,
    retry: 0,
  })

  const projectList = useMemo(() => {
    return projects?.items || []
  }, [projects])

  const { data: taskGroups } = useQuery({
    queryKey: ["get-task-groups-for-task", selectedProjectId],
    queryFn: () =>
      getGroupWiseTaskListService({
        project_id: Number(selectedProjectId),
      }),
    enabled: !!selectedProjectId,
    retry: 0,
  })

  const taskGroupList = useMemo(() => {
    const list: any[] = []

    if (taskGroups) {
      Object.keys(taskGroups).map((key) => {
        if (key == "ungrouped_tasks") {
          list.push({ id: 0, name: "Ungrouped" })
        } else {
          taskGroups[key].map((group: any) => list.push({ id: group.id, name: group.name }))
        }
      })
    }

    return list
  }, [taskGroups])

  const { data: taskStatusOptions } = useQuery({
    queryKey: ["get-task-status-list", orgId],
    queryFn: () =>
      getTaskStatusListService({
        context_type: "organization user",
        context_id: orgId,
      }),
    enabled: !!orgId && !taskStatusList?.length,
    retry: 0,
  })

  const { data: taskPriorityOptions } = useQuery({
    queryKey: ["get-task-priority-list", orgId],
    queryFn: () => getTaskPrioritiesListService(orgId),
    enabled: !!orgId && !taskPrioritiesList?.length,
    retry: 0,
  })

  const statusList = taskStatusList?.length ? taskStatusList : taskStatusOptions
  const priorityList = taskPrioritiesList?.length ? taskPrioritiesList : taskPriorityOptions

  const onSubmit = (data: TaskFormValues) => {
    const payload = {
      ...data,
      project_id: Number(data.project_id),
      tasks_group_id: data.tasks_group_id ? Number(data.tasks_group_id) : undefined,
      status_id: data.status_id ? Number(data.status_id) : undefined,
      priority_id: data.priority_id ? Number(data.priority_id) : undefined,
      responsible_id: selectedAssignees.map((u) => u.id),
      inform_users: true,
    }
    createTaskMutation.mutate(payload)
  }

  const createTaskMutation = useMutation({
    mutationKey: ["create-new-task"],
    mutationFn: createNewTaskService,
    onSuccess: (data) => {
      onClose()
      queryClient.invalidateQueries({ queryKey: ["get-task"] })
      queryClient.invalidateQueries({ queryKey: ["get-pinned-task"] })
      queryClient.invalidateQueries({ queryKey: ["get-project-wise-task-list"] })
      queryClient.invalidateQueries({ queryKey: ["get-kanban-task"] })
      toast({
        title: translate("Success"),
        description: `${data?.title} ${translate("task created successfully")}`,
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to create task"),
        variant: "error",
      })
    },
  })

  return (
    <Fragment>
      {/* Header */}
      {!isMobile ? (
        <div className='flex items-center justify-between border-b border-neutral-200 px-5 py-4'>
          <h2 className='font-[inter] text-base font-medium text-neutral-800'>
            {translate("New Task")}
          </h2>
          <CloseCircle className='cursor-pointer text-neutral-800' size={24} onClick={onClose} />
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className={cn("flex flex-col gap-3 overflow-scroll border-b", isMobile ? "p-3" : "p-6")}
            style={isMobile ? {} : { maxHeight: "calc(100vh - 320px)" }}
          >
            {/* Project (required) */}
            <FormField
              control={control}
              name='project_id'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {translate("Project")}: <small className='text-red-500'>*</small>
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(val) => {
                        field.onChange(val)
                        setValue("tasks_group_id", "")
                      }}
                    >
                      <SelectTrigger
                        disabled={isProjectLoading}
                        className={cn(
                          isHebrew ? "rtl" : "ltr",
                          errors?.project_id
                            ? "border-error-300 focus:border-error-500 focus:ring-2 focus:ring-error-500"
                            : "",
                        )}
                      >
                        <SelectValue
                          placeholder={
                            isProjectLoading
                              ? `${translate("Loading")}...`
                              : translate("Select Project")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                        {projectList?.map((project: any, index: number) => (
                          <SelectItem
                            value={String(project?.id)}
                            className='rounded-lg'
                            key={index}
                          >
                            {project?.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Task Title (required) */}
            <FormField
              control={control}
              name='title'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {translate("Task Name")}: <small className='text-red-500'>*</small>
                  </FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      className={cn(
                        "w-full rounded-lg border px-3 py-2 text-sm outline-none",
                        errors?.title
                          ? "border-error-300 focus:border-error-500 focus:ring-2 focus:ring-error-500"
                          : "border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                      )}
                      placeholder={translate("Enter task name")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showMoreFields ? (
              <>
                {/* Task Group */}
                <FormField
                  control={control}
                  name='tasks_group_id'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("Task Group")}</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className={cn(isHebrew ? "rtl" : "ltr")}>
                            <SelectValue placeholder={translate("Select Task Group")} />
                          </SelectTrigger>
                          <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                            {taskGroupList?.map((group: any, index: number) => (
                              <SelectItem
                                value={String(group?.id)}
                                className='rounded-lg'
                                key={index}
                              >
                                {group?.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  {/* Status */}
                  <FormField
                    control={control}
                    name='status_id'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{translate("Status")}</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className={cn(isHebrew ? "rtl" : "ltr")}>
                              <SelectValue placeholder={translate("Select Status")} />
                            </SelectTrigger>
                            <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                              {statusList?.map((status: any, index: number) => (
                                <SelectItem
                                  value={String(status?.id)}
                                  className='rounded-lg'
                                  key={index}
                                >
                                  {translate(status?.name)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Priority */}
                  <FormField
                    control={control}
                    name='priority_id'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{translate("Priority")}</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className={cn(isHebrew ? "rtl" : "ltr")}>
                              <SelectValue placeholder={translate("Select Priority")} />
                            </SelectTrigger>
                            <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                              {priorityList?.map((priority: any, index: number) => (
                                <SelectItem
                                  value={String(priority?.id)}
                                  className='rounded-lg'
                                  key={index}
                                >
                                  {translate(priority?.name)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Date Range */}
                <FormItem>
                  <FormLabel>{translate("Date Range")}</FormLabel>
                  <FormControl>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type='button'
                          className='flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 hover:border-indigo-500 focus:outline-none'
                        >
                          <CalendarDate
                            startDate={watch("start_at")}
                            endDate={watch("finish_to")}
                          />
                          <Calendar className='h-4 w-4' />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className='w-auto p-0' align='start'>
                        <CalendarElement
                          initialFocus
                          mode='range'
                          locale={isHebrew ? he : undefined}
                          numberOfMonths={2}
                          selected={{
                            from:
                              watch("start_at") === "" ? undefined : new Date(watch("start_at")),
                            to:
                              watch("finish_to") === "" ? undefined : new Date(watch("finish_to")),
                          }}
                          onSelect={(range: any) => {
                            if (range?.from) {
                              setValue(
                                "start_at",
                                range.from.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }),
                              )
                            }
                            if (range?.to) {
                              setValue(
                                "finish_to",
                                range.to.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }),
                              )
                            }
                          }}
                        />
                        <div className='flex w-full justify-center p-2'>
                          <PopoverClose>
                            <button
                              type='button'
                              className='w-[200px] rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600'
                            >
                              {translate("Save")}
                            </button>
                          </PopoverClose>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </FormControl>
                </FormItem>

                {/* Description */}
                <FormField
                  control={control}
                  name='description'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("Task Description")}</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          rows={3}
                          className='w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500'
                          placeholder={translate("Enter task description")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Responsible Users (multiple) */}
                <div className='flex items-start gap-4'>
                  <label className='w-1/5 pt-1 text-sm font-medium text-gray-700'>
                    {translate("Responsible")}
                  </label>
                  <div className='w-4/5'>
                    <div className='flex flex-wrap gap-3'>
                      {selectedAssignees.map((user) => (
                        <div key={user.id} className='flex items-center gap-4'>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label
                                className={cn(
                                  "flex cursor-pointer items-center gap-1 rounded-full border border-neutral-200 py-1",
                                  isHebrew ? "pl-4 pr-1" : "pl-1 pr-4",
                                )}
                              >
                                <Avatar className='h-7 w-7'>
                                  <AvatarImage
                                    src={
                                      user?.user_picture?.url ||
                                      `${process.env.PUBLIC_URL}/user-profile.svg`
                                    }
                                  />
                                </Avatar>
                                {user?.name}
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent className='bg-indigo-600'>{user?.name}</TooltipContent>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                    <button
                      type='button'
                      className='mt-3 flex items-center gap-2 rounded-xl border border-neutral-200 px-4 py-2 text-sm font-medium shadow hover:text-indigo-700'
                      onClick={() => setAssigneesDialog(true)}
                    >
                      <Add className='h-4 w-4' />
                      {translate("Assign")}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p
                onClick={() => setShowMoreFields(true)}
                className='cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-700'
              >
                {translate("Add more info to the task")}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className={cn("flex items-center justify-end gap-3", isMobile ? "p-3" : "p-6")}>
            <button
              className='rounded-full border border-neutral-200 px-6 py-2 font-[inter] text-sm font-normal text-neutral-800'
              type='button'
              onClick={onClose}
            >
              {translate("Cancel")}
            </button>
            <button
              className='flex rounded-full bg-indigo-600 px-6 py-2 font-[inter] text-sm font-medium text-white shadow'
              type='submit'
              disabled={createTaskMutation.isPending}
              style={{
                background:
                  "linear-gradient(0deg, var(--Indigo-8, #444CE7), var(--Indigo-8, #444CE7)), linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
              }}
            >
              {createTaskMutation.isPending ? (
                <Loader className={cn("animate-spin", isHebrew ? "ml-1" : "mr-1")} size={16} />
              ) : null}{" "}
              {translate("Save")}
            </button>
          </div>
        </form>
      </Form>

      {assigneesDialog && (
        <TeamMembersDialog
          teamMembersDialog={assigneesDialog}
          selectedTeamMembers={selectedAssignees}
          setTeamMembersDialog={setAssigneesDialog}
          setSelectedTeamMembers={setSelectedAssignees}
        />
      )}
    </Fragment>
  )
}

export default TaskInsertDialog
